
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, setCsrfToken } from '../api/client';
import { authApi } from '../api/endpoints';
import type { Id, LoginRequest, SessionResponse, User, UserRole } from '../types/api';
import { hasRole } from '../types/api';
import { queryKeys } from './keys';

export function useMe(): UseQueryResult<SessionResponse, ApiError> {
  return useQuery<SessionResponse, ApiError>({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const session = await authApi.me();
      setCsrfToken(session.csrf_token);
      return session;
    },
    retry: (failureCount, error) => !error.isUnauthorized && failureCount < 2,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useCurrentUser(): {
  user: User | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  can: (min: UserRole) => boolean;
  isAdmin: boolean;
  canChange: (item: { created_by: Id | null }) => boolean;
} {
  const query = useMe();
  const user = query.data?.user;
  const isAdmin = user?.role === 'admin';
  return {
    user,
    isLoading: query.isLoading,
    isAuthenticated: Boolean(user),
    can: (min: UserRole) => hasRole(user, min),
    isAdmin,
    // Mirrors the server rule: only the author or an admin edits or deletes an item.
    canChange: (item) =>
      hasRole(user, 'editor') && (isAdmin || (item.created_by !== null && item.created_by === user?.id)),
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<SessionResponse, ApiError, LoginRequest>({
    mutationFn: (payload) => authApi.login(payload),
    onSuccess: (session) => {
      setCsrfToken(session.csrf_token);
      qc.setQueryData(queryKeys.me, session);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      setCsrfToken(null);
      qc.clear();
    },
  });
}

export function describeLoginError(error: ApiError | null): string | null {
  if (!error) return null;
  if (error.isRateLimited) {
    const seconds = error.retryAfterSeconds;
    return seconds
      ? `Слишком много попыток. Повторите через ${Math.ceil(seconds / 60)} мин.`
      : 'Слишком много попыток. Повторите позже.';
  }
  if (error.isUnauthorized) return 'Неверная почта или пароль';
  return error.message;
}
